import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useColorScheme,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { applicationSlice } from '../../../redux/ApplicationSlice';
import { projectSlice } from '../../../redux/ProjectSlice';
import { RootState } from '../../../redux/Store';
import { ApplicationActions, RobotActions, slotNumberFromSlotName } from '../../../utils/constants';
import { duplicateProject } from '../../../utils/projectDuplication';
import { useDriveUnitPortsForProject } from '../../Dashboard/persistDashboardWidgetContent';
import { readSwerveSlotPythonSource } from '../../../utils/readSwerveSlotPythonSource';
import {
  getSaveSlotDisplayProgramName,
  isSaveSlotEmpty,
  parseBpsProjectMetadataFromPythonSource,
  parseProjectLastModifiedToUtcMs,
  saveSlotMatchesProject,
} from '../../../utils/saveSlotProjectId';
import { createSwerveSlotBackupProject } from '../../../utils/swerveSlotBackupProject';
import checkProjectName from '../../../utils/validation/checkProjectName';
import type { Project } from '../../Projects/projects';
import SwerveSlotList from '../../Drawers/SwerveSlotList';
import MainProgramIcon from '../../Icons/Program/main-program.svg?react';
import SwerveIcon from '../../Icons/Robots/swerve_fill_icon.svg?react';

interface LoadFromSwerveDialogProps {
  open: boolean;
  onClose: () => void;
}

interface PendingRevisionLoad {
  slotKey: string;
  project: Project;
  existingProject: Project;
}

const LoadFromSwerveDialog = ({ onClose }: LoadFromSwerveDialogProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const { mode, systemMode } = useColorScheme();
  const colorMode = mode === 'system' ? systemMode : mode;
  const robotState = useSelector((state: RootState) => state.robot.robotState);
  const projects = useSelector((state: RootState) => state.project.projects);
  const [loadingSlotIndex, setLoadingSlotIndex] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pendingRevisionLoad, setPendingRevisionLoad] = useState<PendingRevisionLoad | null>(null);
  const driveUnitPortsForRevisionCopy = useDriveUnitPortsForProject(pendingRevisionLoad?.project.id);
  const isConnected = robotState === RobotActions.STATE.CONNECTED;

  const handleOpenConnectionDrawer = () => {
    dispatch(applicationSlice.actions.onOpenDialog({ dialog: null, options: {} }));
    dispatch(projectSlice.actions.onOpenDrawer(ApplicationActions.Drawer.CONNECTION_DRAWER));
  };

  const getDisplayName = (programTitle: string, projectId?: string) => {
    if (!programTitle?.trim()) return null;
    return checkProjectName(programTitle.trim(), projectId || '');
  };

  const commitLoadedProject = (slotKey: string, project: Project, existingProject?: Project, selectSourceSaveSlot = true) => {
    if (existingProject) {
      dispatch(projectSlice.actions.onReplaceProject(project));
    } else {
      dispatch(projectSlice.actions.onAddProject(project));
    }
    dispatch(projectSlice.actions.onLoadProject(project));
    if (selectSourceSaveSlot) {
      dispatch(projectSlice.actions.setSelectedSaveSlot(slotNumberFromSlotName(slotKey)));
    }
    dispatch(applicationSlice.actions.onOpenDialog({ dialog: null, options: {} }));
    window.projectId = project.id;
    onClose();
    navigate(`/editor/${project.id}`);
  };

  const handleConfirmRevisionLoad = () => {
    if (!pendingRevisionLoad) return;
    commitLoadedProject(pendingRevisionLoad.slotKey, pendingRevisionLoad.project, pendingRevisionLoad.existingProject);
    setPendingRevisionLoad(null);
  };

  const handleCancelRevisionLoad = () => {
    setPendingRevisionLoad(null);
  };

  const handleCreateRevisionCopy = () => {
    if (!pendingRevisionLoad) return;
    const copyName = t('projects.copyOf', 'Copy of {{name}}', { name: pendingRevisionLoad.project.name });
    const copiedProject = duplicateProject(pendingRevisionLoad.project, copyName, driveUnitPortsForRevisionCopy);
    commitLoadedProject(pendingRevisionLoad.slotKey, copiedProject, undefined, false);
    setPendingRevisionLoad(null);
  };

  const handleLoadSlot = async (
    slotKey: string,
    programTitle: string | undefined,
    bpsProjectId: string | undefined,
    bpsProjectLastModifiedUtcMs: number | undefined
  ) => {
    if (loadingSlotIndex !== null || pendingRevisionLoad !== null) return;
    setLoadingSlotIndex(slotKey);
    setLoadError(null);
    try {
      const pythonSource = await readSwerveSlotPythonSource(slotKey);
      const parsedProjectMetadata = parseBpsProjectMetadataFromPythonSource(pythonSource);
      const slotProjectLastModifiedUtcMs = parsedProjectMetadata.projectLastModifiedUtcMs ?? bpsProjectLastModifiedUtcMs;
      const baseProject = await createSwerveSlotBackupProject({
        pythonSource,
        slot: { programTitle, bpsProjectId, bpsProjectLastModifiedUtcMs },
        fallbackName: slotKey,
      });
      const dedupedName = checkProjectName(baseProject.name, baseProject.id || '');
      const existingProject = baseProject.id ? projects.find((p: Project) => p.id === baseProject.id) : undefined;
      const project: Project = existingProject ? { ...existingProject, ...baseProject, name: dedupedName } : { ...baseProject, name: dedupedName };

      if (existingProject) {
        const existingProjectLastModifiedUtcMs = parseProjectLastModifiedToUtcMs(existingProject.lastModified);
        const matchesExistingRevision = saveSlotMatchesProject(
          {
            isEmpty: false,
            programTitle,
            bpsProjectId: project.id,
            bpsProjectLastModifiedUtcMs: slotProjectLastModifiedUtcMs,
          },
          existingProject.id,
          existingProjectLastModifiedUtcMs
        );

        if (!matchesExistingRevision) {
          setPendingRevisionLoad({ slotKey, project, existingProject });
          return;
        }
      }

      commitLoadedProject(slotKey, project, existingProject);
    } catch (error) {
      console.error('Failed to load project from Drive Unit slot', error);
      setLoadError(error instanceof Error ? error.message : String(error));
    } finally {
      setLoadingSlotIndex(null);
    }
  };

  return (
    <>
      <DialogTitle variant="sectionTitle" align="center">
        <Trans i18nKey="loadFromSwerveDialog.title">Load from Swerve Drive Robot</Trans>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ padding: '24px' }}>
        {!isConnected ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
              <SwerveIcon
                style={{
                  width: 64,
                  height: 64,
                  color: colorMode === 'dark' ? theme.vars?.palette?.common?.white : theme.vars?.palette?.primary?.main,
                }}
              />
            </Box>
            <Typography variant="body1" paragraph>
              <Trans i18nKey="loadFromSwerveDialog.connectPrompt">
                Connect to your Swerve Drive Robot to load a project. The connection panel has been opened on the right.
              </Trans>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <Trans i18nKey="loadFromSwerveDialog.connectHint">After connecting, return here to select a project from a slot.</Trans>
            </Typography>
            <Box sx={{ mt: 3 }}>
              <Button variant="contained" onClick={handleOpenConnectionDrawer}>
                <Trans i18nKey="loadFromSwerveDialog.connectButton">Connect to Robot</Trans>
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <Trans i18nKey="loadFromSwerveDialog.selectSlot">Select a slot to load its project:</Trans>
            </Typography>
            {loadError && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }} role="alert">
                <Trans i18nKey="loadFromSwerveDialog.loadError">Could not load project from Drive Unit. Please try again.</Trans>
              </Typography>
            )}
            <SwerveSlotList
              renderSlot={(slot, slotKey) => {
                const hasProject = !isSaveSlotEmpty(slot);
                const displayName = hasProject ? getDisplayName(getSaveSlotDisplayProgramName(slot?.programTitle), slot?.bpsProjectId) : null;
                const isRowLoading = loadingSlotIndex === slotKey;
                const isAnotherRowLoading = loadingSlotIndex !== null && loadingSlotIndex !== slotKey;
                const slotNumber = slotNumberFromSlotName(slotKey);
                const slotSecondaryLabel = t('slot.selectedSlot', 'Slot {{slotNumber}}', { slotNumber: String(slotNumber) });
                return (
                  <ListItemButton
                    disabled={!hasProject || isAnotherRowLoading}
                    onClick={() => {
                      if (hasProject) {
                        void handleLoadSlot(slotKey, slot?.programTitle, slot?.bpsProjectId, slot?.bpsProjectLastModifiedUtcMs);
                      }
                    }}
                    sx={{ borderRadius: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {isRowLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <Box
                          component={MainProgramIcon}
                          aria-hidden
                          sx={{
                            width: 22,
                            height: 22,
                            display: 'block',
                            color: hasProject ? 'text.primary' : 'text.disabled',
                            flexShrink: 0,
                            opacity: hasProject ? 1 : 0.5,
                          }}
                        />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={hasProject ? displayName : <Trans i18nKey="loadFromSwerveDialog.emptySlot">Empty Slot</Trans>}
                      primaryTypographyProps={{
                        variant: 'body2',
                        color: hasProject ? 'text.primary' : 'text.disabled',
                        fontStyle: hasProject ? undefined : 'italic',
                      }}
                      secondary={hasProject ? slotSecondaryLabel : undefined}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItemButton>
                );
              }}
            />
          </>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'center', padding: 2 }}>
        <Button variant="text" onClick={onClose} disabled={loadingSlotIndex !== null}>
          <Trans i18nKey="button.cancel">Cancel</Trans>
        </Button>
      </DialogActions>
      <Dialog open={pendingRevisionLoad !== null} onClose={handleCancelRevisionLoad} maxWidth="sm" fullWidth>
        <DialogTitle variant="dialogTitle" align="center">
          <Trans i18nKey="loadFromSwerveDialog.revisionMismatch.title">Replace saved project?</Trans>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3, px: 3 }}>
          <Stack spacing={2}>
            <DialogContentText>
              <Trans i18nKey="loadFromSwerveDialog.revisionMismatch.body">
                This Drive Base slot belongs to a project already saved here, but it is a different revision. You can create a copy from the Drive
                Base program or replace your saved local project.
              </Trans>
            </DialogContentText>
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ justifyContent: 'center', px: 2, py: 2 }}>
          <Stack direction="row" spacing={2}>
            <Button variant="text" onClick={handleCancelRevisionLoad}>
              <Trans i18nKey="loadFromSwerveDialog.revisionMismatch.cancel">Cancel</Trans>
            </Button>
            <Button variant="outlined" onClick={handleCreateRevisionCopy}>
              <Trans i18nKey="loadFromSwerveDialog.revisionMismatch.copy">Create Copy</Trans>
            </Button>
            <Button variant="contained" color="error" onClick={handleConfirmRevisionLoad}>
              <Trans i18nKey="loadFromSwerveDialog.revisionMismatch.confirm">Load from Drive Base</Trans>
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LoadFromSwerveDialog;
